import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Github, Upload, Loader2, RefreshCw, Trash2, Boxes, ScanSearch, Lightbulb,
  AlertTriangle, CheckCircle2, FileCode2, ChevronRight, Info, BookMarked, Copy, Wand2, FlaskConical, BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';
import CodeClaimSourceDialog from '@/components/CodeClaimSourceDialog';
import CodeDraftDialog from '@/components/CodeDraftDialog';
import CodeFigureCard from '@/components/CodeFigureCard';

const SUPPORT_META = {
  code_supported: { text: 'Verified in code', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30' },
  needs_publication: { text: 'Needs literature reference', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30' },
  needs_validation_study: { text: 'Needs validation study', cls: 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30' },
};

const CATEGORIES = ['inputs', 'algorithms', 'computations', 'outputs', 'limitations'];
const CATEGORY_LABEL = {
  inputs: 'Data inputs', algorithms: 'Algorithms', computations: 'Computations',
  outputs: 'Outputs', limitations: 'Technical limitations',
};

const ConnectCard = ({ userId, projectId, onConnected }) => {
  const [repoUrl, setRepoUrl] = useState('');
  const [ref, setRef] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [zipProgress, setZipProgress] = useState(0);
  const zipRef = useRef(null);

  const connect = async () => {
    if (!repoUrl.trim()) { toast.error('Paste a GitHub repository URL first'); return; }
    setBusy(true); setError(null);
    try {
      const cp = await thesisApi.connectGithubProject(userId, projectId, { repoUrl, ref, token });
      toast.success(`Connected ${cp.repo_name} — ${cp.file_count} relevant files`);
      onConnected(cp);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'object' ? detail : { error: detail || 'Could not connect to the repository.' });
    } finally {
      setBusy(false);
    }
  };

  const uploadZip = async (file) => {
    if (!file) return;
    setBusy(true); setError(null); setZipProgress(0);
    try {
      const cp = await thesisApi.uploadCodeZip(userId, projectId, file, setZipProgress);
      toast.success(`Imported ${cp.repo_name} — ${cp.file_count} relevant files`);
      onConnected(cp);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'object' ? detail : { error: detail || 'Could not read the archive.' });
    } finally {
      setBusy(false); setZipProgress(0);
      if (zipRef.current) zipRef.current.value = '';
    }
  };

  return (
    <Card data-testid="code-connect-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base md:text-lg">
          <Github className="w-4 h-4" />Add a Python project
        </CardTitle>
        <CardDescription>
          Connect a GitHub repository or import a ZIP archive. Only Python sources, tests,
          documentation and configuration files are indexed — the code version is recorded.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-3 gap-2">
          <Input className="sm:col-span-2" placeholder="https://github.com/owner/repo"
            value={repoUrl} onChange={(e) => setRepoUrl(e.target.value)} data-testid="code-repo-url-input" />
          <Input placeholder="branch / tag / commit (optional)" value={ref}
            onChange={(e) => setRef(e.target.value)} data-testid="code-repo-ref-input" />
        </div>
        <Input type="password" placeholder="GitHub access token — only needed for private repositories"
          value={token} onChange={(e) => setToken(e.target.value)} data-testid="code-repo-token-input" />
        <div className="flex flex-wrap gap-2">
          <Button onClick={connect} disabled={busy} data-testid="code-connect-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Github className="w-4 h-4 mr-2" />}
            Connect repository
          </Button>
          <input ref={zipRef} type="file" accept=".zip" className="hidden"
            onChange={(e) => uploadZip(e.target.files?.[0])} data-testid="code-zip-input" />
          <Button variant="outline" disabled={busy} onClick={() => zipRef.current?.click()}
            data-testid="code-zip-btn">
            <Upload className="w-4 h-4 mr-2" />
            {zipProgress ? `Uploading ${zipProgress}%` : 'Import ZIP archive'}
          </Button>
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 space-y-2"
            data-testid="code-connect-error">
            <p className="text-sm text-destructive flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{error.error}
            </p>
            {error.suggest_zip && (
              <Button size="sm" variant="outline" onClick={() => zipRef.current?.click()}
                data-testid="code-error-zip-btn">
                <Upload className="w-4 h-4 mr-2" />Import the project as a ZIP instead
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ComponentPicker = ({ userId, codeProject, onIndexing }) => {
  const [keys, setKeys] = useState(codeProject.selected_component_keys || []);
  const [extra, setExtra] = useState('');
  const [name, setName] = useState(codeProject.selected_component_name || '');
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const autoMatch = async () => {
    setSuggesting(true);
    try {
      const { suggestions: sg } = await thesisApi.suggestCodeComponent(userId, codeProject.id);
      setSuggestions(sg);
      if (!sg.length) toast.error('No confident match — pick the component manually');
      else {
        setKeys(sg[0].component_keys);
        setName(sg[0].name || sg[0].component_labels?.join(' + ') || '');
        toast.success('Best match preselected from your manuscript topic');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-match failed');
    } finally {
      setSuggesting(false);
    }
  };

  const toggle = (k) => setKeys((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const submit = async () => {
    if (!keys.length && !extra.trim()) { toast.error('Pick at least one component or enter a path'); return; }
    setBusy(true);
    try {
      const res = await thesisApi.selectCodeComponent(userId, codeProject.id, {
        componentKeys: keys,
        extraPaths: extra.split(',').map((s) => s.trim()).filter(Boolean),
        componentName: name,
      });
      toast.success(`Indexing ${res.selected_file_count} files for “${res.selected_component_name}”`);
      onIndexing();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start indexing');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="code-component-picker">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base md:text-lg">
          <Boxes className="w-4 h-4" />Choose the component to write about
        </CardTitle>
        <CardDescription>
          Detected from directory structure and filename patterns. Select one or more — the whole
          repository is never used indiscriminately.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <Button size="sm" variant="outline" onClick={autoMatch} disabled={suggesting}
            data-testid="code-automatch-btn">
            {suggesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            Match to my article topic
          </Button>
          <span className="text-xs text-muted-foreground">
            Or tick components manually — you can combine 2–3 for one article.
          </span>
        </div>

        {suggestions.length > 0 && (
          <div className="space-y-2" data-testid="code-automatch-suggestions">
            {suggestions.map((sg, i) => (
              <button key={i} type="button"
                onClick={() => { setKeys(sg.component_keys); setName(sg.name || ''); }}
                data-testid={`code-suggestion-${i}`}
                className="w-full text-left rounded-lg border border-primary/30 bg-primary/5 p-3 hover:border-primary">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="bg-primary/10 border-primary/30">
                    {sg.component_labels?.join(' + ') || sg.name}
                  </Badge>
                  <span className="text-[11px] text-muted-foreground">
                    confidence {(sg.confidence * 100).toFixed(0)}%
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{sg.rationale}</p>
              </button>
            ))}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-2 max-h-80 overflow-y-auto pr-1" data-testid="code-components-list">
          {(codeProject.components || []).map((c) => (
            <button key={c.key} type="button" onClick={() => toggle(c.key)}
              data-testid={`code-component-${c.key}`}
              className={`text-left rounded-lg border p-3 transition-colors ${
                keys.includes(c.key)
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/40 hover:bg-muted/40'}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium truncate">{c.name}</span>
                <Badge variant="outline" className="text-[10px] shrink-0">{c.kind}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {c.file_count} files · {c.source_file_count} Python
              </p>
              <p className="text-[11px] text-muted-foreground/80 font-mono truncate mt-1">
                {(c.sample_paths || [])[0]}
              </p>
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-2">
          <Input placeholder="Component label, e.g. Breast Health Intelligence"
            value={name} onChange={(e) => setName(e.target.value)} data-testid="code-component-name-input" />
          <Input placeholder="Extra paths (comma separated), e.g. backend/services/bhi_scoring.py"
            value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="code-extra-paths-input" />
        </div>

        <Button onClick={submit} disabled={busy} data-testid="code-index-component-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanSearch className="w-4 h-4 mr-2" />}
          Index selected component
        </Button>
      </CardContent>
    </Card>
  );
};

const SoftwareCitationCard = ({ userId, codeProjectId }) => {
  const [data, setData] = useState(null);
  const [style, setStyle] = useState('vancouver');

  useEffect(() => {
    thesisApi.codeSoftwareReference(userId, codeProjectId).then(setData).catch(() => {});
  }, [userId, codeProjectId]);

  if (!data) return null;
  const text = data.formatted?.[style] || '';

  return (
    <Card data-testid="code-software-citation">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base md:text-lg">
          <BookMarked className="w-4 h-4" />Cite the software itself
        </CardTitle>
        <CardDescription>
          Generated from the repository, the recorded version and commit
          {data.from_citation_cff ? ' plus the project CITATION.cff' : ''}. It is appended to the
          project bibliography automatically under “Software”.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex gap-2 flex-wrap">
          {['vancouver', 'apa7', 'ama', 'bibtex'].map((st) => (
            <Button key={st} size="sm" variant={style === st ? 'default' : 'outline'}
              onClick={() => setStyle(st)} data-testid={`software-style-${st}`}>
              {st === 'apa7' ? 'APA 7' : st === 'ama' ? 'AMA 11' : st === 'bibtex' ? 'BibTeX' : 'Vancouver'}
            </Button>
          ))}
        </div>
        <pre className="text-xs whitespace-pre-wrap break-words border border-border rounded-lg p-3 bg-muted/30"
          data-testid="software-reference-text">{text}</pre>
        <Button size="sm" variant="outline" data-testid="software-copy-btn"
          onClick={() => { navigator.clipboard.writeText(text); toast.success('Reference copied'); }}>
          <Copy className="w-4 h-4 mr-2" />Copy reference
        </Button>
      </CardContent>
    </Card>
  );
};

export const CodeProjectPanel = ({ userId, projectId, language = 'en', chapters = [], onInserted, onOpenChapter }) => {
  const [codeProjects, setCodeProjects] = useState([]);
  const [active, setActive] = useState(null);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [titles, setTitles] = useState([]);
  const [titlesBusy, setTitlesBusy] = useState(false);
  const [resync, setResync] = useState(null);
  const [resyncBusy, setResyncBusy] = useState(false);
  const [usage, setUsage] = useState({});
  const [claimOpen, setClaimOpen] = useState(false);
  const [activeClaim, setActiveClaim] = useState(null);
  const [draftSection, setDraftSection] = useState(null);

  const loadList = useCallback(async () => {
    try {
      const { code_projects } = await thesisApi.listCodeProjects(userId, projectId);
      setCodeProjects(code_projects);
      if (code_projects.length) {
        const fresh = await thesisApi.getCodeProject(userId, code_projects[0].id);
        setActive(fresh);
        setShowPicker(fresh.status === 'inventory');
      } else {
        setActive(null);
      }
    } finally {
      setLoading(false);
    }
  }, [userId, projectId]);

  const loadClaims = useCallback(async (cpId) => {
    const data = await thesisApi.listCodeClaims(userId, cpId);
    setClaims(data.claims);
    try {
      const u = await thesisApi.codeClaimUsage(userId, cpId);
      setUsage(u.usage || {});
    } catch (e) {
      setUsage({});
    }
  }, [userId]);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    if (!active) return;
    if (active.status === 'ready' && active.claim_count > 0) loadClaims(active.id);
  }, [active, loadClaims]);

  // Poll while indexing or extracting facts
  useEffect(() => {
    if (!active) return undefined;
    const pending = active.status === 'indexing' || active.facts_status === 'running';
    if (!pending) { setExtracting(active.facts_status === 'running'); return undefined; }
    const t = setInterval(async () => {
      const fresh = await thesisApi.getCodeProject(userId, active.id);
      setActive(fresh);
      if (fresh.status === 'ready') setShowPicker(false);
      if (fresh.facts_status !== 'running') {
        setExtracting(false);
        if (fresh.facts_status === 'done') loadClaims(fresh.id);
        if (fresh.facts_status === 'error') toast.error(fresh.facts_message || 'Fact extraction failed');
      }
    }, 4000);
    return () => clearInterval(t);
  }, [active, userId, loadClaims]);

  const startExtract = async () => {
    setExtracting(true);
    try {
      await thesisApi.extractCodeFacts(userId, active.id, language);
      setActive((a) => ({ ...a, facts_status: 'running', facts_message: 'Analysing the selected component…' }));
    } catch (e) {
      setExtracting(false);
      toast.error(e?.response?.data?.detail || 'Could not start the analysis');
    }
  };

  const loadTitles = async () => {
    setTitlesBusy(true);
    try {
      const { titles: t } = await thesisApi.codeTitleIdeas(userId, active.id, language);
      setTitles(t);
      if (!t.length) toast.error('No titles were returned. Please retry.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate titles');
    } finally {
      setTitlesBusy(false);
    }
  };

  const runResync = async () => {
    setResyncBusy(true);
    try {
      const res = await thesisApi.resyncCodeProject(userId, active.id);
      setResync(res);
      if (res.stale_claim_count) {
        toast.warning(`${res.stale_claim_count} claim(s) need author review`);
        loadClaims(active.id);
      } else {
        toast.success(res.message);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.error : detail || 'Resync failed');
    } finally {
      setResyncBusy(false);
    }
  };

  const remove = async () => {
    await thesisApi.deleteCodeProject(userId, active.id);
    setActive(null); setClaims([]); setTitles([]); setResync(null);
    toast.success('Python project removed');
    loadList();
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground" data-testid="code-panel-loading">Loading…</p>;
  }

  if (!active) {
    return (
      <div className="space-y-4 min-w-0 max-w-full overflow-x-hidden" data-testid="code-project-panel">
        <ConnectCard userId={userId} projectId={projectId} onConnected={(cp) => { setActive(cp); setShowPicker(true); loadList(); }} />
      </div>
    );
  }

  const staleCount = claims.filter((c) => c.status === 'stale').length;
  const multiComponent = (active.selected_component_keys || []).length > 1;
  const componentNames = [...new Set(claims.map((c) => c.component_name).filter(Boolean))];

  return (
    <div className="space-y-4 min-w-0 max-w-full overflow-x-hidden" data-testid="code-project-panel">
      <Card data-testid="code-project-header">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                {active.source_type === 'github' ? <Github className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                <span className="truncate">{active.repo_name}</span>
              </CardTitle>
              <CardDescription className="mt-1">
                {active.version ? `version ${active.version} (${active.version_source}) · ` : ''}
                {active.commit_sha ? `commit ${active.commit_sha.slice(0, 10)} · ` : ''}
                {active.file_count} relevant of {active.total_file_count} files
                {active.ref ? ` · ref ${active.ref}` : ''}
              </CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              {active.source_type === 'github' && (
                <Button size="sm" variant="outline" onClick={runResync} disabled={resyncBusy}
                  data-testid="code-resync-btn">
                  {resyncBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Check for a new version
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setShowPicker((s) => !s)}
                data-testid="code-change-component-btn">
                <Boxes className="w-4 h-4 mr-2" />Change component
              </Button>
              <Button size="sm" variant="ghost" onClick={remove} data-testid="code-delete-btn">
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <Badge variant="outline" data-testid="code-status-badge">{active.status}</Badge>
            {active.selected_component_name && (
              <Badge variant="outline" className="bg-primary/10 border-primary/30">
                {active.selected_component_name}
              </Badge>
            )}
            {active.status === 'indexing' && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            <span className="text-muted-foreground text-xs">{active.status_message}</span>
          </div>
          {(active.skipped || []).length > 0 && (
            <p className="text-xs text-muted-foreground flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {active.skipped.length} file(s) were skipped (size or type limits) and are not used for any statement.
            </p>
          )}
        </CardContent>
      </Card>

      {showPicker && (
        <ComponentPicker userId={userId} codeProject={active}
          onIndexing={() => { setActive((a) => ({ ...a, status: 'indexing' })); setClaims([]); setTitles([]); }} />
      )}

      {active.status === 'ready' && (
        <Card data-testid="code-facts-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <ScanSearch className="w-4 h-4" />Verifiable technical statements
            </CardTitle>
            <CardDescription>
              Extracted from {active.indexed_file_count} indexed files ({active.chunk_count} chunks).
              Each statement links to a file and line range; unsupported statements are labelled.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              <Button onClick={startExtract} disabled={extracting} data-testid="code-extract-facts-btn">
                {extracting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analysing (1–4 min)…</>
                  : <>{claims.length ? 'Re-analyse the code' : 'Extract statements from the code'}</>}
              </Button>
              {claims.length > 0 && (
                <>
                  <Button variant="outline" onClick={() => setDraftSection('methods')}
                    data-testid="code-draft-methods-btn">Draft Methods</Button>
                  <Button variant="outline" onClick={() => setDraftSection('system_description')}
                    data-testid="code-draft-system-btn">Draft System description</Button>
                  <Button variant="outline" onClick={loadTitles} disabled={titlesBusy}
                    data-testid="code-titles-btn">
                    {titlesBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lightbulb className="w-4 h-4 mr-2" />}
                    Title ideas
                  </Button>
                </>
              )}
            </div>
            {extracting && <p className="text-xs text-muted-foreground">{active.facts_message}</p>}

            {active.component_summary && (
              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <p className="text-xs font-semibold text-muted-foreground mb-1">Component summary</p>
                <p className="text-sm leading-relaxed" data-testid="code-component-summary">{active.component_summary}</p>
              </div>
            )}

            {staleCount > 0 && (
              <div className="rounded-lg border border-orange-500/40 bg-orange-500/10 p-3 text-sm flex items-start gap-2"
                data-testid="code-stale-warning">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-orange-600" />
                {staleCount} statement(s) rest on files that changed in a newer code version and need your review.
              </div>
            )}

            {multiComponent && componentNames.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="code-component-legend">
                <span className="text-xs text-muted-foreground">Components in this article:</span>
                {componentNames.map((n) => (
                  <Badge key={n} variant="outline" className="text-[10px] bg-primary/10 border-primary/30">
                    {n} · {claims.filter((c) => c.component_name === n).length} statements
                  </Badge>
                ))}
              </div>
            )}

            {CATEGORIES.filter((cat) => claims.some((c) => c.category === cat)).map((cat) => (
              <div key={cat} className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {CATEGORY_LABEL[cat]}
                </p>
                {claims.filter((c) => c.category === cat).map((c) => {
                  const meta = SUPPORT_META[c.support_type];
                  return (
                    <button key={c.id} type="button"
                      onClick={() => { setActiveClaim(c); setClaimOpen(true); }}
                      data-testid={`code-claim-${c.marker}`}
                      className="w-full text-left rounded-lg border border-border p-3 hover:border-primary/50 hover:bg-muted/40 transition-colors">
                      <div className="flex items-start gap-2">
                        <Badge variant="outline" className="font-mono text-[10px] shrink-0">{c.marker}</Badge>
                        <span className="text-sm leading-relaxed flex-1 min-w-0 break-words">{c.claim_text}</span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                      </div>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge variant="outline" className={`text-[10px] ${meta.cls}`}>{meta.text}</Badge>
                        {c.status === 'stale' && (
                          <Badge variant="outline" className="text-[10px] bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30">
                            Needs review
                          </Badge>
                        )}
                        {(usage[c.marker] || []).length > 0 && (
                          <Badge variant="outline" className="text-[10px] bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30"
                            data-testid={`code-claim-usage-${c.marker}`}>
                            in {usage[c.marker].length} section{usage[c.marker].length > 1 ? 's' : ''}
                          </Badge>
                        )}
                        {c.attached_pmid && (
                          <Badge variant="outline" className="text-[10px]">@pmid:{c.attached_pmid}</Badge>
                        )}
                        {(c.evidence_kinds || []).includes('test') && (
                          <Badge variant="outline" className="text-[10px] bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30">
                            <FlaskConical className="w-3 h-3 mr-1" />test-backed
                          </Badge>
                        )}
                        {((c.evidence_kinds || []).includes('doc') || (c.evidence_kinds || []).includes('config')) && (
                          <Badge variant="outline" className="text-[10px]">
                            <BookOpen className="w-3 h-3 mr-1" />docs/config
                          </Badge>
                        )}
                        {multiComponent && c.component_name && (
                          <Badge variant="outline" className="text-[10px]">{c.component_name}</Badge>
                        )}
                        <span className="text-[11px] text-muted-foreground font-mono truncate min-w-0 max-w-full">
                          <FileCode2 className="w-3 h-3 inline mr-1" />
                          {(c.evidence || []).map((e) => `${e.file_path}:${e.start_line}`).join(' · ')}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}

            {(active.results_gaps || []).length > 0 && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2"
                data-testid="code-results-gaps">
                <p className="text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />Missing data for a Results section
                </p>
                <p className="text-xs text-muted-foreground">
                  The code contains no empirical results. No metrics, patients or clinical-effectiveness
                  conclusions are generated. Supply the following yourself:
                </p>
                <ul className="text-sm space-y-1 list-disc pl-5">
                  {active.results_gaps.map((g, i) => <li key={i}>{g}</li>)}
                </ul>
              </div>
            )}

            {titles.length > 0 && (
              <div className="rounded-lg border border-border p-3 space-y-2" data-testid="code-title-ideas">
                <p className="text-sm font-semibold flex items-center gap-2"><Lightbulb className="w-4 h-4" />Title ideas</p>
                {titles.map((t, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-3.5 h-3.5 mt-1 shrink-0 text-primary" />
                    <span>
                      {t.title}
                      <span className="text-xs text-muted-foreground"> — {t.article_type}{t.angle ? `; ${t.angle}` : ''}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {active.status === 'ready' && claims.length > 0 && (
        <CodeFigureCard userId={userId} codeProject={active} language={language}
          chapters={chapters} onInserted={onInserted} />
      )}

      {active.status === 'ready' && <SoftwareCitationCard userId={userId} codeProjectId={active.id} />}

      {resync && (
        <Card data-testid="code-resync-report">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Version check</CardTitle>
            <CardDescription>{resync.message}</CardDescription>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <p>Indexed commit: <span className="font-mono">{(resync.indexed_commit || '—').slice(0, 10)}</span></p>
            <p>Latest commit: <span className="font-mono">{(resync.latest_commit || '—').slice(0, 10)}</span></p>
            {resync.changed_files?.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Changed files: {resync.changed_files.slice(0, 8).join(', ')}
                {resync.changed_files.length > 8 ? ` +${resync.changed_files.length - 8} more` : ''}
              </p>
            )}
            {(resync.stale_locations || []).length > 0 && (
              <div className="pt-2 space-y-2" data-testid="code-stale-locations">
                <p className="text-xs font-semibold text-muted-foreground">
                  Affected passages in your manuscript
                </p>
                {resync.stale_locations.map((loc, i) => (
                  <div key={i} className="flex items-start gap-2 rounded-lg border border-orange-500/30 bg-orange-500/5 p-2">
                    <Badge variant="outline" className="font-mono text-[10px] shrink-0">{loc.marker}</Badge>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{loc.chapter_title}</p>
                      <p className="text-xs text-muted-foreground break-words">{loc.excerpt.slice(0, 200)}…</p>
                    </div>
                    {onOpenChapter && (
                      <Button size="sm" variant="outline" data-testid={`stale-open-chapter-${i}`}
                        onClick={() => onOpenChapter(loc.chapter_id)}>
                        Open section
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <CodeClaimSourceDialog open={claimOpen} onOpenChange={setClaimOpen}
        userId={userId} codeProjectId={active.id} claim={activeClaim}
        usage={usage[activeClaim?.marker] || []}
        onChanged={() => loadClaims(active.id)}
        onOpenChapter={onOpenChapter} />

      {draftSection && (
        <CodeDraftDialog open={!!draftSection} onOpenChange={(v) => !v && setDraftSection(null)}
          userId={userId} codeProject={active} section={draftSection} language={language}
          chapters={chapters} onInserted={onInserted} />
      )}
    </div>
  );
};

export default CodeProjectPanel;
